import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@sp-client/environments/environment';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from '@sp-client/app/services/auth.service';
import { LoginService } from '@sp-client/app/services/login.service';
import { LogService } from '@sp-client/app/services/log.service';

export const firebaseAuthInterceptor: HttpInterceptorFn = (req, next) => {
	const authService = inject(AuthService);
	const loginService = inject(LoginService);
	const log = inject(LogService);

	// Only attach Firebase bearer tokens to calls to our API.
	const isApiRequest = req.url.startsWith(environment.apiUrl);
	if (!isApiRequest) return next(req);

	// If the request already has an auth header, don't override it.
	if (req.headers.has('Authorization')) return next(req);

	// Preferred path: use the token already tracked by our AuthService (onIdTokenChanged).
	const token = authService.token();
	if (token) {
		return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
	}

	// If Firebase Auth hasn't finished rehydrating yet, wait briefly (avoids "null token" races after Google login).
	if (loginService.tokenRehydratedSignal() === false) {
		log.debug('Waiting for Firebase auth rehydration before sending request', { url: req.url });
		return toObservable(loginService.tokenRehydratedSignal).pipe(
			filter(v => v === true),
			take(1),
			timeout({ first: 2000 }),
			switchMap(() => {
				const hydratedToken = authService.token();
				if (!hydratedToken) return next(req);
				return next(req.clone({ setHeaders: { Authorization: `Bearer ${hydratedToken}` } }));
			}),
			catchError(() => next(req))
		);
	}

	// If we believe the user is logged in but the token hasn't arrived yet (common right after social login),
	// wait briefly for the token signal to populate, then attach it.
	if (loginService.loggedInSignal() === true) {
		log.debug('Waiting for Firebase token before sending request', { url: req.url });
		return toObservable(authService.token).pipe(
			filter((t): t is string => typeof t === 'string' && t.length > 0 && t !== 'undefined' && t !== 'null'),
			take(1),
			timeout({ first: 2500 }),
			switchMap(t => next(req.clone({ setHeaders: { Authorization: `Bearer ${t}` } }))),
			catchError(() => next(req))
		);
	}

	// Not logged in (or we don't know yet) -> don't attach Authorization header.
	// Note: req.withCredentials is still set by credentialsInterceptor; this is purely bearer auth.
	return next(req);
};
// @Injectable()
// export class AuthInterceptor implements HttpInterceptor {
// 	intercept(req: HttpRequest<any>, next: HttpHandler) {
// 		const token = localStorage.getItem('token');
// 		if (token) {
// 			const clonedRequest = req.clone({
// 				headers: req.headers.set('Authorization', `Bearer ${token}`)
// 			});
// 			return next.handle(clonedRequest);
// 		}
// 		return next.handle(req);
// 	}
// }

// export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
// 	const token = localStorage.getItem('token');
// 	if (token) {
// 		const clonedRequest = req.clone({
// 			headers: req.headers.set('Authorization', `Bearer ${token}`)
// 		});
// 		return next(clonedRequest);
// 	}
// 	return next(req);
// }
